/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useEffect, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { apiRequest } from '@/lib/api'

type SessionState = {
  session: Session | null
  user: User | null
  loading: boolean
  error: string | null
}

const SessionContext = createContext<SessionState>({
  session: null,
  user: null,
  loading: true,
  error: null,
})

const GUEST_CREDENTIALS_KEY = 'sbor.guest.credentials'

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    const bootstrap = async () => {
      const signInWithStoredGuest = async () => {
        const raw = window.localStorage.getItem(GUEST_CREDENTIALS_KEY)

        if (!raw) {
          return false
        }

        const parsed = JSON.parse(raw) as { email?: string; password?: string }
        if (!parsed.email || !parsed.password) {
          window.localStorage.removeItem(GUEST_CREDENTIALS_KEY)
          return false
        }

        const result = await supabase.auth.signInWithPassword({
          email: parsed.email,
          password: parsed.password,
        })

        if (result.error || !result.data.session) {
          window.localStorage.removeItem(GUEST_CREDENTIALS_KEY)
          return false
        }

        if (mounted) {
          setSession(result.data.session)
          setLoading(false)
        }

        return true
      }

      const createGuestSession = async () => {
        const credentials = await apiRequest<{ email: string; password: string }>('/api/session/bootstrap', {
          method: 'POST',
        })

        window.localStorage.setItem(GUEST_CREDENTIALS_KEY, JSON.stringify(credentials))

        const result = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        })

        if (result.error || !result.data.session) {
          throw new Error(result.error?.message ?? 'Не получилось создать гостевую сессию.')
        }

        if (mounted) {
          setSession(result.data.session)
          setLoading(false)
        }
      }

      const { data, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        setError(sessionError.message)
      }

      if (data.session) {
        if (mounted) {
          setSession(data.session)
          setLoading(false)
        }
        return
      }

      try {
        const restored = await signInWithStoredGuest()
        if (restored) {
          return
        }

        await createGuestSession()
      } catch (bootstrapError) {
        if (mounted) {
          setError(bootstrapError instanceof Error ? bootstrapError.message : 'Не получилось запустить гостевую сессию.')
          setLoading(false)
        }
        return
      }
    }

    bootstrap()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setLoading(false)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  return (
    <SessionContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        error,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export const useSession = () => useContext(SessionContext)
